import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { useErp } from "@/context/ErpContext";
import {
  PencilSimple, ArrowCounterClockwise, Plus, X, CheckCircle, Prohibit,
} from "@phosphor-icons/react";

const METHODS = ["GET", "POST"];
const CATEGORIES = ["core", "finance_efficiency"];

const Drawer = ({ row, onClose, onSaved }) => {
  const isNew = !row || row._new;
  const [form, setForm] = useState({
    agent_key: row?.agent_key || "",
    method: row?.effective?.method || "GET",
    oracle_path: row?.effective?.path || "",
    display_name: row?.effective?.name || "",
    required_hint: row?.effective?.required || "",
    category: row?.effective?.category || "core",
    hint: row?.effective?.hint || "",
    enabled: row?.effective?.enabled ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (isNew) {
        await api.post("/erp-agents/tenant-config", form);
      } else {
        await api.put(`/erp-agents/tenant-config/${form.agent_key}`, {
          method: form.method, oracle_path: form.oracle_path, display_name: form.display_name,
          required_hint: form.required_hint, category: form.category, hint: form.hint,
          enabled: form.enabled,
        });
      }
      await onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white text-gray-900 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-300"
        data-testid="agent-setup-drawer">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">
              {isNew ? "Create Custom Agent" : `Edit Agent · ${row?.agent_key}`}
            </div>
            <h3 className="text-2xl font-black tracking-tight mt-1">
              {isNew ? "New tenant-scoped agent" : form.display_name || row?.agent_key}
            </h3>
          </div>
          <button onClick={onClose} data-testid="agent-setup-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {isNew && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Agent Key <span className="text-[#DC2626]">*</span></label>
              <input value={form.agent_key} onChange={(e) => setForm({ ...form, agent_key: e.target.value })}
                data-testid="agent-setup-key" placeholder="e.g. custom-po-report (lowercase, hyphens)"
                className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Display Name</label>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="agent-setup-name"
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="agent-setup-category"
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Method</label>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                data-testid="agent-setup-method"
                className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]">
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Oracle REST Path</label>
              <input value={form.oracle_path} onChange={(e) => setForm({ ...form, oracle_path: e.target.value })}
                data-testid="agent-setup-path" placeholder="/fscmRestApi/resources/11.13.18.05/..."
                className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Required Fields / LLM Hint</label>
            <textarea value={form.required_hint} onChange={(e) => setForm({ ...form, required_hint: e.target.value })}
              data-testid="agent-setup-required" rows={4}
              placeholder="Describe the Oracle payload schema or query format the LLM should build."
              className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Prompt Placeholder (shown in user&apos;s run modal)</label>
            <input value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })}
              data-testid="agent-setup-hint" placeholder='e.g. "List approved POs for the last 30 days"'
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              data-testid="agent-setup-enabled" className="w-4 h-4 accent-[#0020C2]" />
            <span className="font-medium">Enabled for tenant users</span>
          </label>
          {err && <div className="text-xs text-[#DC2626] border border-[#DC2626] bg-red-50 px-3 py-2" data-testid="agent-setup-err">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} data-testid="agent-setup-save"
              className="bg-[#0020C2] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
              {saving ? "Saving…" : (isNew ? "Create custom agent" : "Save changes")}
            </button>
            <button onClick={onClose} className="border border-gray-300 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AgentSetupPage() {
  const { fetchCatalog } = useErp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyKey, setBusyKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/erp-agents/tenant-config");
      setRows(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = async () => { await load(); await fetchCatalog(); };

  const toggle = async (row) => {
    setBusyKey(row.agent_key);
    try {
      await api.put(`/erp-agents/tenant-config/${row.agent_key}`, {
        enabled: !row.effective.enabled,
      });
      await onSaved();
    } finally { setBusyKey(""); }
  };

  const resetDefault = async (row) => {
    if (!window.confirm(row.effective.is_custom
      ? `Delete custom agent "${row.agent_key}"? This cannot be undone.`
      : `Revert "${row.agent_key}" to baked-in defaults?`)) return;
    setBusyKey(row.agent_key);
    try {
      await api.delete(`/erp-agents/tenant-config/${row.agent_key}`);
      await onSaved();
    } finally { setBusyKey(""); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Admin Console"
        title="Agent Setup"
        subtitle="Configure the Oracle REST path, method, prompt hint and visibility for every ERP agent in your tenant. You can also create brand-new tenant-scoped agents here."
        testid="agent-setup-title"
        actions={
          <button onClick={() => setEditing({ _new: true })} data-testid="agent-setup-new-btn"
            className="bg-[#0020C2] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> New Custom Agent
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="agent-setup-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Agent Key", "Display Name", "Category", "Method", "Oracle Path", "Enabled", "Type", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.agent_key} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`agent-row-${r.agent_key}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.agent_key}</td>
                  <td className="px-4 py-3 font-medium">{r.effective.name}</td>
                  <td className="px-4 py-3"><span className="text-[10px] tracking-[0.15em] uppercase font-bold text-gray-600">{r.effective.category}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{r.effective.method}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-gray-700 max-w-xs truncate" title={r.effective.path}>{r.effective.path}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(r)} disabled={busyKey === r.agent_key}
                      data-testid={`agent-toggle-${r.agent_key}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wider uppercase border ${
                        r.effective.enabled
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                      {r.effective.enabled ? <><CheckCircle size={10} weight="fill" /> Enabled</> : <><Prohibit size={10} weight="fill" /> Disabled</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold ${
                      r.effective.is_custom ? "text-[#0020C2]" : (r.effective.overridden ? "text-orange-600" : "text-gray-500")
                    }`}>
                      {r.effective.is_custom ? "Custom" : (r.effective.overridden ? "Overridden" : "Baked-in")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditing(r)} data-testid={`agent-edit-${r.agent_key}`}
                        className="text-[11px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline inline-flex items-center gap-1">
                        <PencilSimple size={11} weight="bold" /> Edit
                      </button>
                      {(r.effective.overridden || r.effective.is_custom) && (
                        <button onClick={() => resetDefault(r)} disabled={busyKey === r.agent_key}
                          data-testid={`agent-reset-${r.agent_key}`}
                          className="text-[11px] font-bold uppercase tracking-wider text-[#DC2626] hover:underline inline-flex items-center gap-1">
                          <ArrowCounterClockwise size={11} weight="bold" />
                          {r.effective.is_custom ? "Delete" : "Reset"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  {loading ? "Loading…" : "No agents configured."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <Drawer row={editing.agent_key ? editing : null} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
